import React, { useState, useEffect, useCallback } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';

interface FileSearchBarProps {
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
  debounceMs?: number; // 기본 300ms
}

/**
 * 파일 검색바 컴포넌트
 * - 실시간 검색 (Debounce 처리)
 * - 검색어 초기화 버튼
 * - 300ms 디바운스 (성능 최적화)
 */
const FileSearchBar: React.FC<FileSearchBarProps> = ({
  onSearch,
  placeholder = '파일 이름, 태그, 설명으로 검색...',
  debounceMs = 300,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs, onSearch]);

  // 검색어 초기화
  const handleClear = useCallback(() => {
    setSearchTerm('');
  }, []);

  // 입력 변경
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  return (
    <TextField
      fullWidth
      size="small"
      value={searchTerm}
      onChange={handleChange}
      placeholder={placeholder}
      variant="outlined"
      sx={{
        bgcolor: 'background.paper',
        '& .MuiOutlinedInput-root': {
          height: 40,
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: searchTerm && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClear}
              edge="end"
              aria-label="검색어 지우기"
            >
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default FileSearchBar;
